import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Sunrise, Zap, Film, Sparkles } from 'lucide-react';
import axios from 'axios';

const API = '';

const EarlyBirdBanner = () => {
  const [earlyBird, setEarlyBird] = useState(null);
  const [trapSpadePromo, setTrapSpadePromo] = useState(null);
  const [fourPlusOne, setFourPlusOne] = useState(null);
  const [bonusHours, setBonusHours] = useState(null);
  const bannerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/early-bird-deal`);
        setEarlyBird(r.data);
      } catch (e) {
        console.error('Error checking early bird deal:', e);
      }
      try {
        const r2 = await axios.get(`${API}/api/trapspade-deal-status`);
        setTrapSpadePromo(r2.data);
      } catch (e) {
        console.error('Error checking Trap Spade promo:', e);
      }
      try {
        const r3 = await axios.get(`${API}/api/deals/four-plus-one`);
        setFourPlusOne(r3.data);
      } catch (e) {
        console.error('Error checking 4+1 deal:', e);
      }
      try {
        const r4 = await axios.get(`${API}/api/deals/bonus-hours`);
        setBonusHours(r4.data);
      } catch (e) {
        console.error('Error checking bonus-hours deal:', e);
      }
    })();
  }, []);

  const showEarlyBird = !!(earlyBird && earlyBird.is_active);
  const showTrapSpade = !!(trapSpadePromo && trapSpadePromo.is_active && (trapSpadePromo.remaining_slots > 0 || trapSpadePromo.is_unlimited));
  const showFourPlusOne = !!(fourPlusOne && fourPlusOne.is_active);
  const showBonusHours = !!(bonusHours && bonusHours.is_active);
  const bonusMin = Number(bonusHours?.tiers?.[0]?.min_hours) || 5;
  const bonusLabel = bonusHours?.tiers?.[0]?.bonus || '1 Free Reel';
  // PB Slimeyy + Wick lineup banner — auto-shows only during October
  const showSeptember = new Date().getMonth() === 9;

  // Measure the actual rendered banner height so the Header can sit flush against it.
  useLayoutEffect(() => {
    const applyHeight = () => {
      const h = bannerRef.current ? bannerRef.current.getBoundingClientRect().height : 0;
      document.documentElement.style.setProperty('--banner-h', `${Math.ceil(h)}px`);
    };
    applyHeight();
    if (!bannerRef.current || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', applyHeight);
      return () => window.removeEventListener('resize', applyHeight);
    }
    const ro = new ResizeObserver(applyHeight);
    ro.observe(bannerRef.current);
    window.addEventListener('resize', applyHeight);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', applyHeight);
      document.documentElement.style.setProperty('--banner-h', '0px');
    };
  }, [showEarlyBird, showTrapSpade, showFourPlusOne, showBonusHours, showSeptember]);

  if (!showEarlyBird && !showTrapSpade && !showFourPlusOne && !showBonusHours && !showSeptember) {
    return null;
  }

  const engineerLabel = (earlyBird?.engineer_names && earlyBird.engineer_names.length > 0)
    ? earlyBird.engineer_names.join(' or ')
    : 'select engineers';

  return (
    <>
      <div ref={bannerRef} className="fixed top-0 left-0 right-0 z-[60]">
        {showSeptember && (
          <div className="relative overflow-hidden bg-gradient-to-r from-indigo-900 via-purple-700 to-fuchsia-600 text-white py-2 sm:py-2.5 px-2 sm:px-4 shadow-lg border-b border-fuchsia-400/30">
            {/* subtle grain overlay */}
            <div
              className="absolute inset-0 opacity-[0.08] pointer-events-none"
              style={{
                backgroundImage:
                  "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)",
                backgroundSize: "3px 3px",
              }}
            />
            <div className="relative max-w-7xl mx-auto flex items-center justify-center gap-1.5 sm:gap-3 text-center">
              <Sparkles className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-yellow-200 flex-shrink-0 animate-pulse" fill="currentColor" />
              {/* Mobile compact */}
              <span className="sm:hidden text-xs whitespace-nowrap">
                <span className="font-bold text-yellow-200">OCTOBER · $30/HR</span>
                <span className="mx-1">·</span>
                <span>PB Slimeyy + Wick</span>
              </span>
              {/* Desktop full */}
              <span className="hidden sm:flex items-center gap-2.5">
                <span className="font-black tracking-wide text-yellow-200">OCTOBER LINEUP · $30/HR</span>
                <span className="text-fuchsia-200">|</span>
                <span>
                  <span className="font-bold text-white">PB Slimeyy</span>
                  <span className="text-fuchsia-100 mx-1">—</span>
                  <span className="text-yellow-100">$150 Feature Verse</span>
                </span>
                <span className="text-fuchsia-200">·</span>
                <span>
                  <span className="font-bold text-white">WickTheProducer</span>
                  <span className="text-fuchsia-100 mx-1">—</span>
                  <span className="text-yellow-100">Custom Beats $50 ea</span>
                </span>
              </span>
              <a
                href="/book"
                data-testid="september-lineup-book-btn"
                className="bg-yellow-300 text-purple-900 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full font-black text-[10px] sm:text-sm hover:bg-yellow-200 transition-colors flex-shrink-0"
              >
                Book →
              </a>
            </div>
          </div>
        )}

        {showBonusHours && (
          <div className="bg-gradient-to-r from-purple-700 via-fuchsia-600 to-pink-500 text-white py-2 sm:py-2.5 px-2 sm:px-4 shadow-lg">
            <div className="max-w-7xl mx-auto flex items-center justify-center gap-1.5 sm:gap-3 text-center">
              <Film className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-yellow-100 flex-shrink-0" />
              {/* Mobile compact */}
              <span className="sm:hidden text-xs whitespace-nowrap">
                <span className="font-bold">{bonusHours?.month?.toUpperCase() || 'MONTHLY'} DEAL</span>
                <span className="mx-1">·</span>
                <span data-testid="bonus-banner-mobile-text">Book {bonusMin}hr+ = {bonusLabel}</span>
              </span>
              {/* Desktop full */}
              <span className="hidden sm:flex items-center gap-2.5">
                <span className="font-bold tracking-wide">{bonusHours?.month?.toUpperCase() || 'MONTHLY'} DEAL</span>
                <span className="text-yellow-100">|</span>
                <span data-testid="bonus-banner-desktop-text">
                  Book <span className="font-bold">{bonusMin}+ hours</span> get{' '}
                  <span className="font-bold text-yellow-200">{bonusLabel}</span>
                </span>
                {bonusHours?.weekday_deal && (
                  <>
                    <span className="text-yellow-100">|</span>
                    <span data-testid="weekday-deal-banner-text">
                      <span className="font-bold">Mon-Wed:</span> Book 3 hrs get 1 FREE
                    </span>
                  </>
                )}
              </span>
              <a
                href="/book"
                data-testid="bonus-hours-book-btn"
                className="bg-white text-fuchsia-700 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full font-bold text-[10px] sm:text-sm hover:bg-fuchsia-50 transition-colors flex-shrink-0"
              >
                Book
              </a>
            </div>
          </div>
        )}

        {showFourPlusOne && (
          <div className="bg-gradient-to-r from-purple-600 via-fuchsia-600 to-yellow-500 text-white py-2 sm:py-2.5 px-2 sm:px-4 shadow-lg">
            <div className="max-w-7xl mx-auto flex items-center justify-center gap-1.5 sm:gap-3 text-center">
              <Zap className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-yellow-100 flex-shrink-0" fill="currentColor" />
              {/* Mobile compact */}
              <span className="sm:hidden text-xs whitespace-nowrap">
                <span className="font-bold">BOOK 4 GET 1 FREE</span>
                <span className="mx-1">·</span>
                <span className="font-bold text-yellow-100">${Math.min(...(fourPlusOne.engineers?.map(e => e.deal_price) || [160]))}+</span>
              </span>
              {/* Desktop full */}
              <span className="hidden sm:flex items-center gap-2.5">
                <span className="font-bold tracking-wide">BOOK 4 HOURS, GET 1 FREE</span>
                <span className="text-yellow-100">|</span>
                {(fourPlusOne.engineers || []).map((eng, i) => (
                  <span key={eng.id}>
                    <span className="font-bold">{eng.name}</span>{' '}
                    <span className="font-bold text-yellow-100 text-lg">${eng.deal_price}</span>
                    <span className="line-through opacity-70 text-sm ml-1">${eng.regular_price}</span>
                    {i < (fourPlusOne.engineers.length - 1) && <span className="mx-2 text-yellow-100">·</span>}
                  </span>
                ))}
              </span>
              <a
                href="/book"
                data-testid="four-plus-one-book-btn"
                className="bg-white text-purple-700 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full font-bold text-[10px] sm:text-sm hover:bg-purple-50 transition-colors flex-shrink-0"
              >
                Book
              </a>
            </div>
          </div>
        )}

        {showEarlyBird && (
          <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-pink-500 text-white py-2 sm:py-2.5 px-2 sm:px-4 shadow-lg">
            <div className="max-w-7xl mx-auto flex items-center justify-center gap-1.5 sm:gap-3 text-center">
              <Sunrise className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-yellow-100 flex-shrink-0" />
              {/* Mobile */}
              <span className="sm:hidden text-xs whitespace-nowrap">
                <span className="font-bold">EARLY BIRD</span>
                <span className="mx-1">·</span>
                {earlyBird.hours || 6}hr w/ {engineerLabel}
                <span className="mx-1">·</span>
                <span className="font-bold text-yellow-100">${earlyBird.deal_price}</span>
              </span>
              {/* Desktop */}
              <span className="hidden sm:flex items-center gap-2.5">
                <span className="font-bold tracking-wide">EARLY BIRD SPECIAL</span>
                <span className="text-amber-100">|</span>
                <span>
                  <span className="font-bold">{earlyBird.hours || 6} Hours</span> with{' '}
                  <span className="font-bold">{engineerLabel}</span>:{' '}
                  <span className="font-bold text-yellow-100 text-lg">${earlyBird.deal_price}</span>{' '}
                  <span className="line-through opacity-70 text-sm ml-1">${earlyBird.regular_price}</span>
                </span>
                <span className="bg-white/95 text-orange-600 px-2 py-0.5 rounded-full text-xs font-bold">
                  {earlyBird.start_time}–{earlyBird.end_time}
                </span>
              </span>
              <a
                href="/book"
                data-testid="early-bird-book-btn"
                className="bg-white text-orange-600 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full font-bold text-[10px] sm:text-sm hover:bg-orange-50 transition-colors flex-shrink-0"
              >
                Book
              </a>
            </div>
          </div>
        )}

        {showTrapSpade && (
          <div className="bg-gradient-to-r from-purple-700 via-violet-700 to-purple-700 text-white py-2 sm:py-2.5 px-2 sm:px-4 border-t border-purple-400/30">
            <div className="max-w-7xl mx-auto flex items-center justify-center gap-1.5 sm:gap-3 text-center">
              <span className="hidden sm:inline font-bold text-sm">TRAP SPADE DEAL</span>
              <span className="text-sm">
                <span className="font-bold">{trapSpadePromo.hours} Hours</span> with{' '}
                <span className="font-bold">Trap Spade</span>:{' '}
                <span className="font-bold text-yellow-300 text-lg">${trapSpadePromo.deal_price}</span>
              </span>
              {!trapSpadePromo.is_unlimited && (
                <span className="bg-yellow-400 text-purple-900 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-bold">
                  {trapSpadePromo.remaining_slots} left
                </span>
              )}
              <a
                href="/book"
                data-testid="trapspade-book-btn"
                className="bg-white text-purple-700 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full font-bold text-[10px] sm:text-sm hover:bg-purple-50 transition-colors flex-shrink-0"
              >
                Book
              </a>
            </div>
          </div>
        )}
      </div>
      {/* Spacer - exactly matches the banner's measured height (set via CSS var) */}
      <div
        aria-hidden="true"
        style={{ height: 'var(--banner-h, 0px)' }}
      ></div>
    </>
  );
};

export default EarlyBirdBanner;
